import { Sparkles, PlayCircle, Target, TrendingUp, Brain, Bell, Heart } from "lucide-react";
import SidePanelMockup from "./SidePanelMockup";
import { externalLinkProps, installCta, pricingCta } from "../../lib/funnelLinks";

const ChromeIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <circle cx="12" cy="12" r="10" fill="#fff" />
    <circle cx="12" cy="12" r="3.5" fill="#2563EB" />
    <path
      d="M12 2 a10 10 0 0 1 8.66 5 H12 a5 5 0 0 0 -4.33 2.5 z"
      fill="#EF4444"
    />
    <path
      d="M3.34 7 A10 10 0 0 0 7.67 21.5 L12 14 a5 5 0 0 1 -4.33 -2.5 z"
      fill="#22C55E"
    />
    <path
      d="M20.66 7 H12 a5 5 0 0 1 4.33 7.5 L7.67 21.5 A10 10 0 0 0 20.66 7 z"
      fill="#FACC15"
    />
    <circle cx="12" cy="12" r="3.5" fill="#2563EB" />
  </svg>
);

const miniFeatures = [
  { icon: Target, color: "#2563EB", title: "Focus on what matters", sub: "Cut through noise" },
  { icon: TrendingUp, color: "#22C55E", title: "Steady progress", sub: "Small steps, real momentum" },
  { icon: Sparkles, color: "#7C3AED", title: "AI that understands", sub: "Support without overwhelm" },
  { icon: Bell, color: "#F59E0B", title: "Gentle reminders", sub: "Amber nudges, not alarms" },
  { icon: Heart, color: "#F9735B", title: "Kind by design", sub: "Built for your brain" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 px-6 lg:px-10 overflow-hidden"
    >
      {/* Mist gradient backdrop */}
      <div aria-hidden className="absolute inset-0 mr-mist pointer-events-none" />
      <div
        aria-hidden
        className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0) 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 lg:gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full bg-[#DBEAFE] border border-[#2563EB]/15 px-3.5 py-1.5 text-xs font-medium text-[#1d4ed8]"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>
              Built for ADHD.{" "}
              <span className="text-[#0F172A]">Designed for calm.</span>
            </span>
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display mt-6 text-[2.7rem] sm:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-tight font-semibold text-[#0F172A]"
          >
            Get{" "}
            <span className="relative inline-block">
              <span className="relative z-10">unstuck</span>
              <span
                aria-hidden
                className="absolute left-0 right-0 -bottom-1 h-2 bg-[#F59E0B]/55 rounded-full -z-0"
              />
            </span>{" "}
            before
            <br className="hidden sm:block" /> your brain spirals.
          </h1>

          <p
            data-testid="hero-sub"
            className="mt-6 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-2xl"
          >
            Mindrail is your gentle co-pilot for focus, clarity, and
            follow-through. It lives in your browser side panel — noticing
            distraction, frozen moments, overload, and hyperfocus — and helps
            you return with one tiny next step.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href={installCta.href}
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#2563EB] text-white px-7 py-4 text-base font-medium hover:bg-[#1d4ed8] transition-all hover:scale-[1.02] shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              {...externalLinkProps(installCta.external)}
            >
              <ChromeIcon className="w-5 h-5" />
              {installCta.label}
            </a>
            <a
              href={pricingCta.href}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F172A] border border-[#E2E8F0] px-7 py-4 text-base font-medium hover:border-[#2563EB] hover:text-[#2563EB] transition-all"
            >
              <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
              See Pro features
            </a>
          </div>

        </div>

        <div className="lg:col-span-5 fade-up flex justify-center">
          <SidePanelMockup />
        </div>
      </div>

      {/* Mini-feature row */}
      <div className="relative max-w-7xl mx-auto mt-16 lg:mt-24 fade-up">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 lg:gap-6">
          {miniFeatures.map((f, i) => (
            <div
              key={i}
              data-testid={`hero-mini-${i}`}
              className="text-center"
            >
              <div className="flex justify-center">
                <span
                  className="inline-flex items-center justify-center w-12 h-12 rounded-2xl"
                  style={{ background: f.color + "18" }}
                >
                  <f.icon
                    className="w-5 h-5"
                    strokeWidth={1.75}
                    style={{ color: f.color }}
                  />
                </span>
              </div>
              <p className="mt-3 font-display text-sm sm:text-base font-semibold text-[#0F172A]">
                {f.title}
              </p>
              <p className="mt-0.5 text-xs sm:text-sm text-[#475569]">
                {f.sub}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

